import { derivePhases, buildPhaseColors, MS_SHAPES, HEALTH_COLORS } from "@/lib/constants";
import SwatchPicker from "@/components/SwatchPicker";
import { ArrowUp, ArrowDown } from "lucide-react";

export default function ReportRoadmap({ project, tasks, mode = "roadmap", zoom = 100, healthById = {}, onOpenTask, onOpenPhase, onColorChange, onMoveLane }) {
  const bars = tasks.filter((t) => !t.is_summary && !t.is_milestone && t.start_date && t.end_date);
  const miles = tasks.filter((t) => t.is_milestone && (t.start_date || t.end_date));
  const dated = [...bars, ...miles];
  if (!dated.length) return <p className="text-sm text-muted-foreground">No dated tasks or milestones yet.</p>;

  let min = Infinity, max = -Infinity;
  dated.forEach((t) => {
    const s = Date.parse(t.start_date || t.end_date), e = Date.parse(t.end_date || t.start_date);
    if (s < min) min = s;
    if (e > max) max = e;
  });
  const pad = (max - min) * 0.03 + 86400000;
  min -= pad; max += pad;
  const span = Math.max(1, max - min);
  const px = (d) => ((Date.parse(d) - min) / span) * 100;

  const phases = derivePhases(project, tasks);
  const colors = { ...buildPhaseColors(phases), ...(project.phase_colors || {}) };
  const clickable = !!onOpenTask;
  const hcolor = (t) => HEALTH_COLORS[(healthById[t.id] && healthById[t.id].health) || t.health];

  const packLanes = (items, gap = 22) => {
    const laneEnds = [];
    return {
      items: items
        .map((m) => ({ ...m, mx: px(m.start_date || m.end_date) }))
        .sort((a, b) => a.mx - b.mx)
        .map((m) => {
          let lane = laneEnds.findIndex((end) => m.mx > end + gap);
          if (lane === -1) { laneEnds.push(0); lane = laneEnds.length - 1; }
          laneEnds[lane] = m.mx;
          return { ...m, lane };
        }),
      lanes: laneEnds.length,
    };
  };

  // Summarized roadmap = milestones only, grouped into phase swimlanes (like the roadmap view)
  const known = new Set(phases);
  const laneOrder = (project.report_lane_order || []).filter((p) => known.has(p) || p === "Other");
  const orderedPhases = laneOrder.length
    ? [...phases].sort((a, b) => {
        const ia = laneOrder.indexOf(a), ib = laneOrder.indexOf(b);
        return (ia === -1 ? phases.indexOf(a) + 1000 : ia) - (ib === -1 ? phases.indexOf(b) + 1000 : ib);
      })
    : phases;
  const groups = orderedPhases.map((ph) => ({ ph, items: miles.filter((m) => m.phase === ph) }));
  const other = miles.filter((m) => !known.has(m.phase));
  if (other.length) groups.push({ ph: "Other", items: other });
  const msGroups = groups.filter((g) => g.items.length).map((g) => ({ ph: g.ph, ...packLanes(g.items) }));

  const taskRows = mode === "tasks"
    ? [...bars].sort((a, b) => (a.start_date || "").localeCompare(b.start_date || "") || (a.order || 0) - (b.order || 0)).map((t) => ({
        t, left: px(t.start_date), width: Math.max(px(t.end_date) - px(t.start_date), 1),
        pct: Math.round(Number(t.percent_complete) || 0),
      }))
    : [];
  const taskMarks = mode === "tasks" ? packLanes(miles) : { items: [], lanes: 0 };

  const months = [];
  const cur = new Date(min);
  cur.setDate(1);
  while (cur.getTime() <= max) {
    if (cur.getTime() >= min) {
      months.push({
        t: cur.getTime(),
        label: cur.toLocaleString("en-US", { month: "short" }) + ((cur.getMonth() === 0 || months.length === 0) ? ` '${String(cur.getFullYear()).slice(2)}` : ""),
      });
    }
    cur.setMonth(cur.getMonth() + 1);
  }
  const step = months.length > 14 ? Math.ceil(months.length / 14) : 1;
  const ticks = months.filter((_, i) => i % step === 0);

  const Milestone = ({ m }) => (
    <div onClick={() => onOpenTask?.(m)} title={clickable ? "Open in plan" : undefined} className={`absolute flex items-center gap-1 ${clickable ? "cursor-pointer hover:underline" : ""}`} style={{ left: `${Math.min(m.mx, 80)}%`, top: m.lane * 22 + 3 }} data-testid={`report-roadmap-ms-${m.id}`}>
      <span className="text-[9px] tabular-nums font-semibold text-muted-foreground">{(m.start_date || m.end_date).slice(5).replace("-", "/")}</span>
      <span className="text-xs leading-none" style={{ color: hcolor(m) || m.ms_color || "#F59E0B" }}>{MS_SHAPES[m.ms_shape] || "▼"}</span>
      <span className="text-[10px] font-semibold truncate max-w-[160px]">{(m.status === "done" || (m.percent_complete || 0) >= 100) && <span className="text-emerald-500 font-bold">✓ </span>}{m.title}</span>
    </div>
  );

  return (
    <div data-testid="report-mini-roadmap" className="overflow-x-auto">
      <div style={{ width: `${zoom}%`, minWidth: "100%" }}>
        <div className="relative h-5">
          {ticks.map((m) => (
            <span key={m.t} className="absolute text-[9px] font-bold uppercase tracking-wide text-muted-foreground" style={{ left: `${((m.t - min) / span) * 100}%` }}>{m.label}</span>
          ))}
        </div>
        <div className="relative border-t border-border">
          {ticks.map((m) => (
            <span key={m.t} className="absolute top-0 bottom-0 w-px bg-border/50" style={{ left: `${((m.t - min) / span) * 100}%` }} />
          ))}
          {mode === "roadmap" ? (
            msGroups.length === 0 ? (
              <p className="text-sm text-muted-foreground py-3 relative">No milestones yet — switch to “Visual plan (tasks)” to see the task timeline.</p>
            ) : (
              <div className="py-2 space-y-1.5 relative">
                {msGroups.map((g, gi) => (
                  <div key={g.ph} data-testid={`report-roadmap-row-${g.ph}`}>
                    <div className="flex items-center gap-1">
                      <button onClick={() => onOpenPhase?.(g.ph)} className={`text-[10px] font-bold uppercase tracking-wide ${onOpenPhase ? "cursor-pointer hover:underline" : "cursor-default"}`} style={{ color: colors[g.ph] || "#64748B" }} title={onOpenPhase ? "Open project roadmap" : undefined}>
                        {g.ph}
                      </button>
                      {(onColorChange || onMoveLane) && (
                        <span className="inline-flex items-center gap-0.5 print-hide">
                          {onColorChange && <SwatchPicker value={colors[g.ph] || "#64748B"} onChange={(c) => onColorChange(g.ph, c)} title={`${g.ph} lane color`} testid={`report-lane-color-${g.ph}`} />}
                          {onMoveLane && gi > 0 && (
                            <button onClick={() => onMoveLane(g.ph, -1, msGroups.map((x) => x.ph))} className="p-0.5 rounded hover:bg-secondary text-muted-foreground" title="Move lane up" data-testid={`report-lane-up-${g.ph}`}><ArrowUp className="h-3 w-3" /></button>
                          )}
                          {onMoveLane && gi < msGroups.length - 1 && (
                            <button onClick={() => onMoveLane(g.ph, 1, msGroups.map((x) => x.ph))} className="p-0.5 rounded hover:bg-secondary text-muted-foreground" title="Move lane down" data-testid={`report-lane-down-${g.ph}`}><ArrowDown className="h-3 w-3" /></button>
                          )}
                        </span>
                      )}
                    </div>
                    <div className="relative rounded-sm" style={{ height: g.lanes * 22 + 6, background: `${colors[g.ph] || "#64748B"}0D` }}>
                      {g.items.map((m) => <Milestone key={m.id} m={m} />)}
                    </div>
                  </div>
                ))}
              </div>
            )
          ) : (
            <>
              <div className="space-y-0.5 py-2 relative">
                {taskRows.map(({ t, left, width, pct }) => {
                  const bc = hcolor(t) || colors[t.phase] || "#64748B";
                  return (
                    <div key={t.id} onClick={() => onOpenTask?.(t)} title={clickable ? "Open in plan" : undefined} className={`relative h-5 group ${clickable ? "cursor-pointer" : ""}`} data-testid={`report-plan-row-${t.id}`}>
                      <div className="absolute inset-y-0.5 rounded-sm transition-[filter] group-hover:brightness-95" style={{ left: `${left}%`, width: `${width}%`, background: `${bc}40`, border: `1px solid ${bc}` }}>
                        <div className="absolute inset-y-0 left-0 rounded-sm" style={{ width: `${pct}%`, background: bc, opacity: 0.55 }} />
                      </div>
                      <span className={`absolute top-0.5 text-[9px] font-semibold whitespace-nowrap ${clickable ? "group-hover:underline" : ""}`} style={{ left: `calc(${Math.min(left + width, 80)}% + 6px)` }}>{t.title} · {pct}%</span>
                    </div>
                  );
                })}
              </div>
              {taskMarks.items.length > 0 && (
                <div className="relative border-t border-dashed border-border" style={{ height: taskMarks.lanes * 22 + 8 }}>
                  {taskMarks.items.map((m) => <Milestone key={m.id} m={m} />)}
                </div>
              )}
            </>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-4 mt-2 pt-1.5 border-t border-border/60" data-testid="report-roadmap-legend">
          {[["blue", "Closed"], ["green", "On track"], ["yellow", "At risk"], ["red", "Jeopardy"], ["gray", "No committed date"]].map(([k, l]) => (
            <span key={k} className="inline-flex items-center gap-1.5 text-[10px] font-semibold text-muted-foreground">
              <span className="h-2.5 w-2.5 rounded-full" style={{ background: HEALTH_COLORS[k] }} /> {l}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
